import React, { useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { useParams, Link } from 'react-router-dom';
import { SEO } from '../components/shared/SEO';
import {
    BookOpen,
    Calendar,
    ChevronLeft,
    Clock,
    Eye,
    Heart,
    MessageCircle,
    Share2,
    Star,
    Tag,
    User,
} from 'lucide-react';

interface Post {
    slug: string;
    title: string;
    content: string;
    html: string;
    excerpt: string;
    category: string;
    categoryName: string;
    tags: string[];
    author: string;
    date: string;
    readingTime: number;
    views: number;
    uniqueVisitorsToday: number;
    coverImage?: string;
    commentsCount?: number;
    likes?: number;
    favorites?: number;
    viewerLiked?: boolean;
    viewerFavorited?: boolean;
    adjacent?: {
        previous: PostLink | null;
        next: PostLink | null;
    };
}

interface PostLink {
    slug: string;
    title: string;
    date: string;
}

interface AuthorProfile {
    name: string;
    title: string;
    bio: string;
}

interface TocItem {
    id: string;
    text: string;
    level: number;
}

interface CommentItem {
    id: string;
    parentId: string | null;
    author: string;
    content: string;
    createdAt: string;
    replies: CommentItem[];
}

interface ReactionState {
    likes: number;
    favorites: number;
    viewerLiked: boolean;
    viewerFavorited: boolean;
}

function slugifyHeading(value: string) {
    return String(value || '')
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9\u4e00-\u9fa5]+/g, '-')
        .replace(/^-+|-+$/g, '');
}

const BlogPost: React.FC = () => {
    const { slug } = useParams<{ slug: string }>();
    const articleRef = useRef<HTMLElement | null>(null);
    const [post, setPost] = useState<Post | null>(null);
    const [profile, setProfile] = useState<AuthorProfile | null>(null);
    const [loading, setLoading] = useState(true);
    const [toc, setToc] = useState<TocItem[]>([]);
    const [activeSection, setActiveSection] = useState('');
    const [renderedHtml, setRenderedHtml] = useState('');
    const [readingProgress, setReadingProgress] = useState(0);
    const [reactions, setReactions] = useState<ReactionState>({
        likes: 0,
        favorites: 0,
        viewerLiked: false,
        viewerFavorited: false,
    });
    const [comments, setComments] = useState<CommentItem[]>([]);
    const [commentForm, setCommentForm] = useState({
        author: '',
        content: '',
    });
    const [replyTo, setReplyTo] = useState<{ id: string; author: string } | null>(null);
    const [commentSubmitting, setCommentSubmitting] = useState(false);
    const [interactionHint, setInteractionHint] = useState('');

    useEffect(() => {
        window.scrollTo({
            top: 0,
            left: 0,
            behavior: 'auto',
        });
    }, [slug]);

    useEffect(() => {
        const fetchData = async () => {
            if (!slug) {
                setLoading(false);
                return;
            }

            try {
                const [postRes, profileRes, commentsRes] = await Promise.all([
                    fetch(`/api/blog-admin/posts/${slug}`),
                    fetch('/api/blog-admin/profile'),
                    fetch(`/api/blog-admin/posts/${slug}/comments`),
                ]);

                const postData = await postRes.json();
                const profileData = await profileRes.json();
                const commentsData = await commentsRes.json();

                if (postData.success) {
                    setPost(postData.data);
                    setReactions({
                        likes: postData.data.likes || 0,
                        favorites: postData.data.favorites || 0,
                        viewerLiked: Boolean(postData.data.viewerLiked),
                        viewerFavorited: Boolean(postData.data.viewerFavorited),
                    });
                }

                if (profileData.success) {
                    setProfile(profileData.data);
                }

                if (commentsData.success) {
                    setComments(commentsData.data || []);
                }
            } catch (error) {
                console.error('获取文章失败:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [slug]);

    useEffect(() => {
        if (!post?.html) {
            setRenderedHtml('');
            setToc([]);
            return;
        }

        const parser = new DOMParser();
        const doc = parser.parseFromString(post.html, 'text/html');
        const firstHeading = doc.body.querySelector('h1, h2, h3');
        if (
            firstHeading
            && firstHeading.tagName === 'H1'
            && firstHeading.textContent?.trim() === post.title.trim()
        ) {
            firstHeading.remove();
        }

        const headings = doc.querySelectorAll('h1, h2, h3');
        const usedIds = new Set<string>();

        const tocItems = Array.from(headings).map((heading, index) => {
            const existingId = heading.id?.trim();
            const fallbackBaseId = slugifyHeading(heading.textContent || '') || `section-${index + 1}`;
            const baseId = existingId || fallbackBaseId;

            let resolvedId = baseId;
            let duplicateIndex = 2;
            while (usedIds.has(resolvedId)) {
                resolvedId = `${baseId}-${duplicateIndex}`;
                duplicateIndex += 1;
            }

            usedIds.add(resolvedId);
            heading.id = resolvedId;

            return {
                id: resolvedId,
                text: heading.textContent || '',
                level: Number.parseInt(heading.tagName.charAt(1), 10),
            };
        });

        setToc(tocItems);
        setRenderedHtml(doc.body.innerHTML);
    }, [post]);

    useEffect(() => {
        if (!renderedHtml) {
            return undefined;
        }

        const article = articleRef.current;
        if (!article) {
            return undefined;
        }

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setActiveSection(entry.target.id);
                    }
                });
            },
            { rootMargin: '-20% 0% -35% 0%' },
        );

        const headings = article.querySelectorAll('h1[id], h2[id], h3[id]');
        headings.forEach((heading) => observer.observe(heading));

        return () => {
            headings.forEach((heading) => observer.unobserve(heading));
            observer.disconnect();
        };
    }, [renderedHtml]);

    // 代码块复制按钮注入
    useEffect(() => {
        if (!renderedHtml) {
            return;
        }

        const article = articleRef.current;
        if (!article) {
            return;
        }

        // 等待 DOM 更新
        const timer = window.setTimeout(() => {
            const preElements = article.querySelectorAll('pre');
            preElements.forEach((pre) => {
                // 避免重复注入
                if (pre.parentElement?.classList.contains('code-block-wrapper')) {
                    return;
                }

                const wrapper = document.createElement('div');
                wrapper.className = 'code-block-wrapper relative group';

                const code = pre.querySelector('code');
                const originalText = code?.textContent || pre.textContent || '';

                // 创建复制按钮
                const copyBtn = document.createElement('button');
                copyBtn.className = `absolute top-3 right-3 flex items-center gap-1.5 px-3 py-1.5 rounded-lg
                    bg-slate-700/80 hover:bg-slate-600 text-slate-300 hover:text-white
                    text-xs font-medium opacity-0 group-hover:opacity-100 transition-all
                    border border-slate-600 hover:border-slate-500`;
                copyBtn.setAttribute('aria-label', '复制代码');

                // 使用 SVG 图标而非 JSX
                const copySvg = `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="14" height="14" x="8" y="8" rx="2" ry="2"/><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/><\/svg>`;
                const checkSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/><\/svg>`;

                copyBtn.innerHTML = `${copySvg}<span>复制</span>`;

                copyBtn.addEventListener('click', async (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    try {
                        await navigator.clipboard.writeText(originalText);
                        copyBtn.innerHTML = `${checkSvg}<span>已复制</span>`;
                        copyBtn.classList.add('!opacity-100', '!text-emerald-400', '!border-emerald-500/50');
                        setTimeout(() => {
                            copyBtn.innerHTML = `${copySvg}<span>复制</span>`;
                            copyBtn.classList.remove('!opacity-100', '!text-emerald-400', '!border-emerald-500/50');
                        }, 2000);
                    } catch {
                        copyBtn.innerHTML = `<span>复制失败</span>`;
                        setTimeout(() => {
                            copyBtn.innerHTML = `${copySvg}<span>复制</span>`;
                        }, 2000);
                    }
                });

                // 将 pre 包装在 wrapper 中，并添加复制按钮
                pre.parentNode?.insertBefore(wrapper, pre);
                wrapper.appendChild(pre);
                wrapper.appendChild(copyBtn);
            });
        }, 100);

        return () => clearTimeout(timer);
    }, [renderedHtml]);

    const scrollToSection = (sectionId: string, behavior: ScrollBehavior = 'smooth') => {
        const target = document.getElementById(sectionId);
        if (!target) {
            return;
        }

        const navHeight = document.querySelector('nav')?.getBoundingClientRect().height || 0;
        const top = window.scrollY + target.getBoundingClientRect().top - navHeight - 24;

        window.scrollTo({
            top: Math.max(0, top),
            behavior,
        });

        setActiveSection(sectionId);
        window.history.replaceState(null, '', `#${encodeURIComponent(sectionId)}`);
    };

    useEffect(() => {
        if (!renderedHtml) {
            return;
        }

        const hash = decodeURIComponent(window.location.hash.replace(/^#/, ''));
        if (!hash) {
            return;
        }

        requestAnimationFrame(() => {
            scrollToSection(hash, 'auto');
        });
    }, [renderedHtml]);

    useEffect(() => {
        const handleScroll = () => {
            const article = document.querySelector('article');
            if (!article) {
                setReadingProgress(0);
                return;
            }

            const rect = article.getBoundingClientRect();
            const articleTop = window.scrollY + rect.top;
            const articleHeight = article.scrollHeight;
            const viewportHeight = window.innerHeight;
            const scrollable = Math.max(1, articleHeight - viewportHeight * 0.8);
            const consumed = window.scrollY - articleTop + viewportHeight * 0.25;
            const percent = Math.min(100, Math.max(0, (consumed / scrollable) * 100));

            setReadingProgress(percent);
        };

        handleScroll();
        window.addEventListener('scroll', handleScroll, { passive: true });
        window.addEventListener('resize', handleScroll);

        return () => {
            window.removeEventListener('scroll', handleScroll);
            window.removeEventListener('resize', handleScroll);
        };
    }, [renderedHtml]);

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('zh-CN', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
        });
    };

    const authorName = useMemo(() => {
        return profile?.name || post?.author || 'LIGHTING';
    }, [profile, post]);

    const refreshComments = async () => {
        if (!slug) {
            return;
        }

        try {
            const res = await fetch(`/api/blog-admin/posts/${slug}/comments`);
            const data = await res.json();
            if (data.success) {
                setComments(data.data || []);
            }
        } catch (error) {
            console.error('刷新评论失败:', error);
        }
    };

    const handleReaction = async (target: 'like' | 'favorite') => {
        if (!slug) {
            return;
        }

        const action = target === 'like'
            ? (reactions.viewerLiked ? 'unlike' : 'like')
            : (reactions.viewerFavorited ? 'unfavorite' : 'favorite');

        try {
            const res = await fetch(`/api/blog-admin/posts/${slug}/reactions`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action }),
            });
            const data = await res.json();
            if (data.success) {
                setReactions(data.data);
            }
        } catch (error) {
            console.error('更新互动状态失败:', error);
        }
    };

    const handleShare = async () => {
        try {
            await navigator.clipboard.writeText(window.location.href);
            setInteractionHint('文章链接已复制');
            setTimeout(() => setInteractionHint(''), 1800);
        } catch (_error) {
            setInteractionHint('复制失败，请手动复制地址栏');
            setTimeout(() => setInteractionHint(''), 1800);
        }
    };

    const submitComment = async (event: React.FormEvent) => {
        event.preventDefault();
        if (!slug || !commentForm.content.trim()) {
            return;
        }

        setCommentSubmitting(true);
        try {
            const res = await fetch(`/api/blog-admin/posts/${slug}/comments`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    author: commentForm.author,
                    content: commentForm.content,
                    parentId: replyTo?.id || null,
                }),
            });
            const data = await res.json();
            if (!data.success) {
                setInteractionHint(data.error || '评论提交失败');
                return;
            }

            const status = data.moderation?.status;
            setCommentForm((prev) => ({
                ...prev,
                content: '',
            }));
            setReplyTo(null);

            if (status === 'approved') {
                setInteractionHint('评论发布成功');
                await refreshComments();
            } else {
                setInteractionHint('评论已提交，待审核后显示');
            }
        } catch (error) {
            console.error('提交评论失败:', error);
            setInteractionHint('评论提交失败，请稍后重试');
        } finally {
            setCommentSubmitting(false);
            setTimeout(() => setInteractionHint(''), 1800);
        }
    };

    const renderCommentItem = (item: CommentItem, depth = 0) => {
        return (
            <div key={item.id} className={`${depth > 0 ? 'mt-3 ml-4 md:ml-8' : ''}`}>
                <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4 bg-white/70 dark:bg-gray-900/40">
                    <div className="flex items-center justify-between gap-3 mb-2">
                        <div className="text-sm font-medium text-gray-800 dark:text-gray-100">{item.author}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">{formatDate(item.createdAt)}</div>
                    </div>
                    <p className="text-sm leading-6 text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{item.content}</p>
                    <button
                        className="mt-2 text-xs text-blue-600 dark:text-blue-400 hover:underline"
                        onClick={() => setReplyTo({ id: item.id, author: item.author })}
                    >
                        回复
                    </button>
                </div>

                {(item.replies || []).map((reply) => renderCommentItem(reply, depth + 1))}
            </div>
        );
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-lg">加载中...</div>
            </div>
        );
    }

    if (!post) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-center">
                    <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-4">文章不存在</h2>
                    <Link to="/blog" className="text-blue-600 hover:underline">
                        返回文章列表
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
            <SEO 
                title={post.title}
                description={post.excerpt || `阅读关于 ${post.title} 的更多内容`}
                keywords={post.tags?.join(',')}
            />
            <div className="fixed left-0 top-0 z-50 h-1 bg-blue-500 transition-all duration-150" style={{ width: `${readingProgress}%` }} />
            <div className="w-full max-w-7xl mx-auto px-3 py-4 sm:py-6 sm:px-4 md:py-8 min-w-0">
                <div className="grid gap-4 sm:gap-6 lg:grid-cols-4 lg:gap-8 min-w-0">
                    <div className="lg:col-span-3 space-y-4 sm:space-y-6 min-w-0">
                        <motion.div
                            initial={{ opacity: 0, y: -20 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="overflow-hidden rounded-2xl bg-gradient-to-br from-blue-600 to-indigo-700 p-4 sm:p-6 md:rounded-xl md:p-8 lg:p-10 text-white shadow-lg min-w-0"
                        >
                            <Link
                                to="/blog"
                                className="mb-4 sm:mb-6 inline-flex items-center gap-2 text-sm text-blue-200 transition-colors hover:text-white sm:text-base"
                            >
                                <ChevronLeft className="w-4 h-4" />
                                返回文章列表
                            </Link>

                            <h1 className="mb-4 text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold leading-tight md:max-w-[14ch] break-words">{post.title}</h1>

                            <div className="grid grid-cols-1 gap-2 text-xs sm:text-sm text-blue-100 sm:flex sm:flex-wrap sm:items-center sm:gap-4 md:text-base min-w-0">
                                <div className="flex items-center gap-2 min-w-0">
                                    <Calendar className="w-4 h-4" />
                                    {formatDate(post.date)}
                                </div>
                                <div className="flex items-center gap-2 min-w-0">
                                    <Clock className="w-4 h-4" />
                                    {post.readingTime}分钟阅读
                                </div>
                                <div className="flex items-center gap-2 min-w-0">
                                    <BookOpen className="w-4 h-4" />
                                    {authorName}
                                </div>
                                <div className="flex items-center gap-2 min-w-0">
                                    <Eye className="w-4 h-4" />
                                    {post.views} 次阅读
                                </div>
                            </div>

                            <div className="mt-4 flex flex-wrap gap-2 sm:mt-5">
                                <span className="rounded-full bg-white/20 px-3 py-1 text-xs sm:text-sm">{post.categoryName || post.category}</span>
                                {post.tags.map((item) => (
                                    <span key={item} className="rounded-full bg-white/10 px-3 py-1 text-xs sm:text-sm">
                                        {item}
                                    </span>
                                ))}
                            </div>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.2 }}
                            className="min-w-0"
                        >
                            {post.coverImage && (
                                <div className="mb-6 overflow-hidden rounded-xl bg-white shadow-lg dark:bg-gray-800">
                                    <img src={post.coverImage} alt={post.title} className="w-full h-auto object-cover max-h-[500px]" />
                                </div>
                            )}
                            <article ref={articleRef} className="overflow-hidden rounded-xl bg-white p-4 sm:p-5 md:p-8 lg:p-10 shadow-lg dark:bg-gray-800 min-w-0 break-words">
                            <div
                                className="markdown-content prose prose-base sm:prose-lg dark:prose-invert max-w-none break-words
                                    prose-headings:text-gray-900 dark:prose-headings:text-white
                                    prose-headings:leading-tight prose-headings:break-words
                                    prose-p:text-gray-700 dark:prose-p:text-gray-300 prose-p:break-words
                                    prose-a:text-blue-600 dark:prose-a:text-blue-400 prose-a:break-all
                                    prose-code:text-pink-600 dark:prose-code:text-pink-400 prose-code:break-words
                                    prose-pre:bg-gray-900 prose-pre:text-gray-100
                                    prose-pre:rounded-xl prose-pre:overflow-x-auto
                                    prose-pre:p-4 sm:prose-pre:p-5
                                    prose-strong:text-gray-900 dark:prose-strong:text-white
                                    prose-ul:text-gray-700 dark:prose-ul:text-gray-300
                                    prose-ol:text-gray-700 dark:prose-ol:text-gray-300
                                    prose-li:text-gray-700 dark:prose-li:text-gray-300
                                    prose-table:w-full prose-table:text-sm
                                    prose-th:bg-gray-100 dark:prose-th:bg-gray-900
                                    prose-th:px-3 prose-th:py-2 prose-th:border prose-th:border-gray-300 dark:prose-th:border-gray-700
                                    prose-td:px-3 prose-td:py-2 prose-td:border prose-td:border-gray-200 dark:prose-td:border-gray-700
                                    prose-hr:border-gray-300 dark:prose-hr:border-gray-700
                                    prose-blockquote:border-l-blue-500 prose-blockquote:bg-gray-50 dark:prose-blockquote:bg-gray-900
                                    prose-img:rounded-xl prose-img:shadow-lg prose-img:max-w-full prose-img:w-full
                                    [&_pre]:text-[13px] sm:[&_pre]:text-sm
                                    [&_table]:block [&_table]:overflow-x-auto [&_table]:whitespace-nowrap
                                    [&_h1]:scroll-mt-24 [&_h2]:scroll-mt-24 [&_h3]:scroll-mt-24
                                    md:[&_h1]:scroll-mt-28 md:[&_h2]:scroll-mt-28 md:[&_h3]:scroll-mt-28"
                                dangerouslySetInnerHTML={{ __html: renderedHtml }}
                            />

                            <div className="mt-8 border-t border-gray-200 pt-6 dark:border-gray-700 sm:pt-8">
                                <div className="flex flex-col gap-4 sm:gap-5">
                                    <div className="flex flex-wrap gap-3 sm:gap-4">
                                        <button
                                            className={`flex items-center gap-2 rounded-lg px-4 py-2 transition-colors ${
                                                reactions.viewerLiked
                                                    ? 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-300'
                                                    : 'bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600'
                                            }`}
                                            onClick={() => handleReaction('like')}
                                        >
                                            <Heart className="w-5 h-5" />
                                            点赞 {reactions.likes}
                                        </button>
                                        <button
                                            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                                                reactions.viewerFavorited
                                                    ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300'
                                                    : 'bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600'
                                            }`}
                                            onClick={() => handleReaction('favorite')}
                                        >
                                            <Star className="w-5 h-5" />
                                            收藏 {reactions.favorites}
                                        </button>
                                        <button
                                            className="flex items-center gap-2 rounded-lg bg-gray-100 px-4 py-2 transition-colors hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600"
                                            onClick={handleShare}
                                        >
                                            <Share2 className="w-5 h-5" />
                                            分享
                                        </button>
                                    </div>

                                    <div className="flex flex-wrap items-center gap-3 text-sm text-gray-500 dark:text-gray-400">
                                        <span className="flex items-center gap-1">
                                            <Eye className="w-4 h-4" />
                                            {post.views}
                                        </span>
                                        <span className="flex items-center gap-1">
                                            <MessageCircle className="w-4 h-4" />
                                            {post.commentsCount ?? comments.length}
                                        </span>
                                        <span>今日访客 {post.uniqueVisitorsToday}</span>
                                    </div>
                                </div>

                                {interactionHint && (
                                    <div className="mt-3 text-sm text-blue-600 dark:text-blue-400">{interactionHint}</div>
                                )}

                                {(post.adjacent?.previous || post.adjacent?.next) && (
                                    <div className="mt-8 grid gap-4 md:grid-cols-2">
                                        {post.adjacent?.previous ? (
                                            <Link
                                                to={`/blog/${post.adjacent.previous.slug}`}
                                                onClick={() => {
                                                    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
                                                }}
                                                className="block rounded-lg border border-gray-200 dark:border-gray-700 p-4 hover:border-blue-400 transition-colors"
                                            >
                                                <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">上一篇</div>
                                                <div className="font-medium text-gray-900 dark:text-white line-clamp-2">
                                                    {post.adjacent.previous.title}
                                                </div>
                                            </Link>
                                        ) : (
                                            <div className="rounded-lg border border-dashed border-gray-200 dark:border-gray-700 p-4 text-sm text-gray-400">
                                                已经是第一篇
                                            </div>
                                        )}

                                        {post.adjacent?.next ? (
                                            <Link
                                                to={`/blog/${post.adjacent.next.slug}`}
                                                onClick={() => {
                                                    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
                                                }}
                                                className="block rounded-lg border border-gray-200 dark:border-gray-700 p-4 hover:border-blue-400 transition-colors text-right"
                                            >
                                                <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">下一篇</div>
                                                <div className="font-medium text-gray-900 dark:text-white line-clamp-2">
                                                    {post.adjacent.next.title}
                                                </div>
                                            </Link>
                                        ) : (
                                            <div className="rounded-lg border border-dashed border-gray-200 dark:border-gray-700 p-4 text-sm text-gray-400 text-right">
                                                已经是最后一篇
                                            </div>
                                        )}
                                    </div>
                                )}

                                <div className="mt-10 border-t border-gray-200 pt-8 dark:border-gray-700">
                                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">评论区</h3>

                                    <form onSubmit={submitComment} className="space-y-3 mb-6">
                                        {replyTo && (
                                            <div className="text-sm text-blue-600 dark:text-blue-400 flex items-center justify-between">
                                                <span>正在回复 @{replyTo.author}</span>
                                                <button
                                                    type="button"
                                                    className="hover:underline"
                                                    onClick={() => setReplyTo(null)}
                                                >
                                                    取消回复
                                                </button>
                                            </div>
                                        )}

                                        <div className="grid gap-3 sm:grid-cols-1 md:grid-cols-4">
                                            <input
                                                type="text"
                                                value={commentForm.author}
                                                onChange={(event) => setCommentForm((prev) => ({ ...prev, author: event.target.value }))}
                                                placeholder="昵称（可选）"
                                                className="md:col-span-1 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-sm sm:text-base"
                                            />
                                            <textarea
                                                required
                                                value={commentForm.content}
                                                onChange={(event) => setCommentForm((prev) => ({ ...prev, content: event.target.value }))}
                                                placeholder="写下你的看法..."
                                                rows={3}
                                                className="md:col-span-3 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-sm sm:text-base"
                                            />
                                        </div>

                                        <button
                                            type="submit"
                                            disabled={commentSubmitting}
                                            className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
                                        >
                                            {commentSubmitting ? '提交中...' : '提交评论'}
                                        </button>
                                    </form>

                                    <div className="space-y-3">
                                        {comments.length === 0 ? (
                                            <div className="text-sm text-gray-500 dark:text-gray-400">暂无公开评论，欢迎成为第一个留言的人。</div>
                                        ) : (
                                            comments.map((item) => renderCommentItem(item))
                                        )}
                                    </div>
                                </div>
                            </div>
                        </article>
                        </motion.div>
                    </div>

                    <motion.div
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.4 }}
                        className="hidden lg:block lg:col-span-1"
                    >
                        <div className="sticky top-8 space-y-6">
                            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm">
                                <h3 className="font-bold text-gray-800 dark:text-white mb-4">目录</h3>
                                <nav className="space-y-2">
                                    {toc.map((item) => (
                                        <button
                                            key={item.id}
                                            type="button"
                                            onClick={() => scrollToSection(item.id)}
                                            className={`block text-sm transition-colors ${
                                                activeSection === item.id
                                                    ? 'text-blue-600 dark:text-blue-400 font-medium'
                                                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                                            }`}
                                            style={{ paddingLeft: `${(item.level - 1) * 12}px` }}
                                        >
                                            {item.text}
                                        </button>
                                    ))}
                                </nav>
                            </div>

                            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm">
                                <h3 className="font-bold text-gray-800 dark:text-white mb-4">文章信息</h3>
                                <div className="space-y-3 text-sm text-gray-600 dark:text-gray-400">
                                    <div className="flex items-center gap-2">
                                        <User className="w-4 h-4" />
                                        {authorName}
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Clock className="w-4 h-4" />
                                        阅读时长 {post.readingTime} 分钟
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Eye className="w-4 h-4" />
                                        总访问 {post.views}
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Tag className="w-4 h-4" />
                                        标签 {post.tags.length}
                                    </div>
                                </div>
                            </div>

                            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm">
                                <h3 className="font-bold text-gray-800 dark:text-white mb-4">关于作者</h3>
                                <div className="text-sm text-gray-600 dark:text-gray-400 leading-6">
                                    <div className="font-medium text-gray-800 dark:text-white mb-1">
                                        {authorName}
                                    </div>
                                    <div className="mb-2">{profile?.title || '软件工程师'}</div>
                                    <p>{profile?.bio || '这里记录工作、学习、生活与各种阶段性的想法。'}</p>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </div>
        </div>
    );
};

export default BlogPost;
